import React, { useEffect, Children } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type LegalModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
};
export function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: LegalModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-168">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-navy-900/80 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-169">
          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="relative w-full max-w-2xl max-h-[85vh] bg-navy-800 border border-navy-700 rounded-2xl shadow-2xl flex flex-col" data-id="element-170">
            <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-navy-700 sticky top-0 bg-navy-800 rounded-t-2xl" data-id="element-171">
              <h2 id="legal-modal-title" className="text-2xl font-serif text-cream" data-id="element-172">
                {title}
              </h2>
              <button onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full bg-navy-900 hover:bg-teal-500 hover:text-navy-900 text-cream-300/80 flex items-center justify-center transition-colors" data-id="element-173">
                <X className="w-5 h-5" data-id="element-174" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 md:px-8 py-6 text-cream-300/80 leading-relaxed space-y-5 text-[15px]" data-id="element-175">
              {children}
            </div>

            <div className="px-6 md:px-8 py-4 border-t border-navy-700 flex justify-end" data-id="element-176">
              <button onClick={onClose} className="bg-teal-500 hover:bg-teal-400 text-navy-900 font-bold px-6 py-2.5 rounded-lg transition-colors" data-id="element-177">
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function PrivacyPolicyContent() {
  return <>
      <p className="text-cream-300/60 text-sm italic" data-id="element-178">
        Last updated: November 2025
      </p>

      <section className="space-y-2" data-id="element-179">
        <h3 className="text-lg font-bold text-cream" data-id="element-180">1. Introduction</h3>
        <p data-id="element-181">
          This Privacy Policy describes how DiLore Publishing ("we," "us," or
          "our") collects, uses, and protects your personal information when you
          visit our website or purchase The 11PM Spine Reset digital product. By
          using our site, you agree to the terms outlined in this policy.
        </p>
      </section>

      <section className="space-y-2" data-id="element-182">
        <h3 className="text-lg font-bold text-cream" data-id="element-183">
          2. Information We Collect
        </h3>
        <p data-id="element-184">We may collect the following types of information:</p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-185">
          <li data-id="element-186">
            <strong className="text-cream" data-id="element-187">Personal information:</strong> name,
            email address, billing details when you place an order.
          </li>
          <li data-id="element-188">
            <strong className="text-cream" data-id="element-189">Payment information:</strong>{' '}
            processed securely by our third-party payment providers. We do not
            store full credit card numbers.
          </li>
          <li data-id="element-190">
            <strong className="text-cream" data-id="element-191">Usage data:</strong> IP address,
            browser type, device information, pages visited, and time spent on
            the site.
          </li>
          <li data-id="element-192">
            <strong className="text-cream" data-id="element-193">Cookies and tracking:</strong> we use
            cookies and similar technologies to improve your experience and
            measure performance.
          </li>
        </ul>
      </section>

      <section className="space-y-2" data-id="element-194">
        <h3 className="text-lg font-bold text-cream" data-id="element-195">
          3. How We Use Your Information
        </h3>
        <p data-id="element-196">We use your information to:</p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-197">
          <li data-id="element-198">Deliver the digital product you purchased.</li>
          <li data-id="element-199">Process payments and prevent fraud.</li>
          <li data-id="element-200">
            Send transactional emails (order confirmations, delivery, customer
            support).
          </li>
          <li data-id="element-201">
            Send marketing emails about related offers — you may unsubscribe at
            any time.
          </li>
          <li data-id="element-202">Improve our website, products, and customer experience.</li>
          <li data-id="element-203">Comply with legal obligations.</li>
        </ul>
      </section>

      <section className="space-y-2" data-id="element-204">
        <h3 className="text-lg font-bold text-cream" data-id="element-205">
          4. Sharing Your Information
        </h3>
        <p data-id="element-206">
          We do not sell your personal information. We may share data with
          trusted third-party service providers (payment processors, email
          delivery, analytics) strictly for the purposes of operating our
          business. These providers are bound by confidentiality obligations.
        </p>
      </section>

      <section className="space-y-2" data-id="element-207">
        <h3 className="text-lg font-bold text-cream" data-id="element-208">5. Data Security</h3>
        <p data-id="element-209">
          We use SSL encryption, secure payment processing, and
          industry-standard safeguards to protect your data. No method of online
          transmission is 100% secure, but we work diligently to keep your
          information safe.
        </p>
      </section>

      <section className="space-y-2" data-id="element-210">
        <h3 className="text-lg font-bold text-cream" data-id="element-211">6. Your Rights</h3>
        <p data-id="element-212">
          Depending on your jurisdiction, you may have the right to access,
          correct, delete, or restrict the processing of your personal
          information. You may also opt out of marketing communications at any
          time using the unsubscribe link in our emails.
        </p>
      </section>

      <section className="space-y-2" data-id="element-213">
        <h3 className="text-lg font-bold text-cream" data-id="element-214">7. Cookies</h3>
        <p data-id="element-215">
          We use cookies for essential functionality, analytics, and marketing.
          You can control cookie preferences through your browser settings.
          Disabling cookies may affect your experience on the site.
        </p>
      </section>

      <section className="space-y-2" data-id="element-216">
        <h3 className="text-lg font-bold text-cream" data-id="element-217">8. Children's Privacy</h3>
        <p data-id="element-218">
          Our product is intended for adults. We do not knowingly collect
          information from children under 18. If you believe a minor has
          provided us with personal information, please contact us so we can
          remove it.
        </p>
      </section>

      <section className="space-y-2" data-id="element-219">
        <h3 className="text-lg font-bold text-cream" data-id="element-220">
          9. Changes to This Policy
        </h3>
        <p data-id="element-221">
          We may update this Privacy Policy from time to time. Changes will be
          posted on this page with an updated revision date. Your continued use
          of the site constitutes acceptance of the updated policy.
        </p>
      </section>
    </>;
}
export function TermsOfServiceContent() {
  return <>
      <p className="text-cream-300/60 text-sm italic" data-id="element-222">
        Last updated: November 2025
      </p>

      <section className="space-y-2" data-id="element-223">
        <h3 className="text-lg font-bold text-cream" data-id="element-224">1. Acceptance of Terms</h3>
        <p data-id="element-225">
          By accessing this website and/or purchasing The 11PM Spine Reset, you
          agree to be bound by these Terms of Service. If you do not agree,
          please do not use the site or purchase the product.
        </p>
      </section>

      <section className="space-y-2" data-id="element-226">
        <h3 className="text-lg font-bold text-cream" data-id="element-227">2. The Product</h3>
        <p data-id="element-228">
          The 11PM Spine Reset is a digital educational product including an
          eBook and bonus materials. It is delivered electronically upon
          successful payment. The product is for personal, non-commercial use
          only.
        </p>
      </section>

      <section className="space-y-2" data-id="element-229">
        <h3 className="text-lg font-bold text-cream" data-id="element-230">3. Medical Disclaimer</h3>
        <p data-id="element-231">
          The information in The 11PM Spine Reset is for educational and
          informational purposes only. It is not intended as medical advice,
          diagnosis, or treatment. Always consult a qualified healthcare
          professional before beginning any new exercise, nutrition, or wellness
          program — especially if you have a pre-existing medical condition, are
          pregnant, or are taking medication. By using the product, you
          acknowledge that you assume all risks associated with following any of
          the protocols.
        </p>
      </section>

      <section className="space-y-2" data-id="element-232">
        <h3 className="text-lg font-bold text-cream" data-id="element-233">4. Results Disclaimer</h3>
        <p data-id="element-234">
          Individual results vary. Testimonials and outcomes shared on this page
          reflect personal experiences and are not guarantees. We make no
          representation that you will achieve identical results.
        </p>
      </section>

      <section className="space-y-2" data-id="element-235">
        <h3 className="text-lg font-bold text-cream" data-id="element-236">5. Pricing & Payment</h3>
        <p data-id="element-237">
          All prices are listed in USD unless otherwise noted. We reserve the
          right to change prices at any time. Payment is processed securely
          through third-party processors at checkout.
        </p>
      </section>

      <section className="space-y-2" data-id="element-238">
        <h3 className="text-lg font-bold text-cream" data-id="element-239">6. Refund Policy</h3>
        <p data-id="element-240">
          We offer a 60-day money-back guarantee. If you are unsatisfied with
          your purchase within 60 days of the order date, you may request a full
          refund. Refunds are processed back to the original payment method
          within a reasonable timeframe.
        </p>
      </section>

      <section className="space-y-2" data-id="element-241">
        <h3 className="text-lg font-bold text-cream" data-id="element-242">
          7. Intellectual Property
        </h3>
        <p data-id="element-243">
          All content — including text, graphics, logos, eBooks, audio, and
          video — is the property of DiLore Publishing and is protected by
          copyright and intellectual property laws. You may not reproduce,
          redistribute, resell, or share the product without written permission.
        </p>
      </section>

      <section className="space-y-2" data-id="element-244">
        <h3 className="text-lg font-bold text-cream" data-id="element-245">8. Account & Access</h3>
        <p data-id="element-246">
          You are responsible for keeping your purchase confirmation and
          download links secure. Sharing access with others is a violation of
          these Terms and may result in revocation of access.
        </p>
      </section>

      <section className="space-y-2" data-id="element-247">
        <h3 className="text-lg font-bold text-cream" data-id="element-248">
          9. Limitation of Liability
        </h3>
        <p data-id="element-249">
          To the maximum extent permitted by law, DiLore Publishing shall not be
          liable for any indirect, incidental, special, or consequential damages
          arising from your use of the site or product. Our total liability is
          limited to the amount you paid for the product.
        </p>
      </section>

      <section className="space-y-2" data-id="element-250">
        <h3 className="text-lg font-bold text-cream" data-id="element-251">10. User Conduct</h3>
        <p data-id="element-252">
          You agree not to use the site for unlawful purposes, attempt to gain
          unauthorized access, disrupt service, or violate any applicable laws
          or third-party rights.
        </p>
      </section>

      <section className="space-y-2" data-id="element-253">
        <h3 className="text-lg font-bold text-cream" data-id="element-254">
          11. Changes to These Terms
        </h3>
        <p data-id="element-255">
          We may update these Terms of Service at any time. Continued use of the
          site constitutes acceptance of any revisions. Please review this page
          periodically.
        </p>
      </section>

      <section className="space-y-2" data-id="element-256">
        <h3 className="text-lg font-bold text-cream" data-id="element-257">12. Governing Law</h3>
        <p data-id="element-258">
          These Terms are governed by applicable laws, without regard to
          conflict of law principles. Any disputes shall be resolved through
          binding arbitration where permitted by law.
        </p>
      </section>
    </>;
}